import { config } from "@config/config";
import { useQueryClient } from "@tanstack/react-query";
import { isSyncDone } from "@utils/learningTreeReview";
import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import { ApiLearnerManualGoalSyncStatusChoices, useLearnerManualGoalSyncByIdQuery } from "@api/graphql/types-and-hooks";
import { ImportSuccessToast } from "@pages/ClinicalReportDetails/RefreshingData/ImportSuccessToast";

const BACKGROUND_SYNC_CONTAINER_ID = "background-sync";

interface SyncInfo {
  learnerId: string;
  reportId: string;
  clientFirstName: string;
}

interface BackgroundSyncContextI {
  startBackgroundSync: (info: SyncInfo) => void;
  clearBackgroundSync: () => void;
}

const BackgroundSyncContext = createContext<BackgroundSyncContextI | undefined>(undefined);

export const useBackgroundSync = () => {
  const context = useContext(BackgroundSyncContext);
  if (context === undefined) {
    throw new Error("useBackgroundSync must be wrapped with BackgroundSyncProvider");
  }
  return context;
};

export const BackgroundSyncProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [syncInfo, setSyncInfo] = useState<SyncInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const handledRef = useRef(false);
  const failureHandledRef = useRef(false);
  // Ignore a Failure left over from a previous sync of the same learner until
  // the freshly-started workflow reports a non-failure status (or a grace
  // period elapses) — mirrors EHRProgrammingRefresh's awaitingFreshSync guard.
  const awaitingFreshSync = useRef(true);
  const awaitingFreshSyncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: importStatusData } = useLearnerManualGoalSyncByIdQuery(
    { learnerId: syncInfo?.learnerId || "" },
    {
      gcTime: 0,
      staleTime: 0,
      refetchInterval: (query) => {
        const status = query?.state?.data?.learnerById?.manualGoalSyncStatus;
        if (
          isSyncDone(status) ||
          (status === ApiLearnerManualGoalSyncStatusChoices.Failure && !awaitingFreshSync.current)
        ) {
          return false;
        }
        return config.REPORT_POLLING_INTERVAL;
      },
      enabled: !!syncInfo?.learnerId,
    },
  );

  const isOnRefreshingDataPage = location.pathname.includes("/refreshing-data");
  const importStatus = importStatusData?.learnerById?.manualGoalSyncStatus;
  const isCompleted = isSyncDone(importStatus);

  // Once the fresh workflow reports any non-failure status, a later Failure is
  // real (not the stale leftover).
  useEffect(() => {
    if (importStatus && importStatus !== ApiLearnerManualGoalSyncStatusChoices.Failure) {
      awaitingFreshSync.current = false;
      if (awaitingFreshSyncTimeout.current) {
        clearTimeout(awaitingFreshSyncTimeout.current);
        awaitingFreshSyncTimeout.current = null;
      }
    }
  }, [importStatus]);

  // Background reassessment import failed mid-flight. This provider keeps
  // polling across navigation, so it — not the now-unmounted refresh screen —
  // is where a credential failure is observed. Refetch ehr_connection so the
  // failure surfaces on the top EHR badge without a manual refresh. For org
  // username/password orgs the workflow has marked the org credentials
  // CREDENTIALS_FAILED; for other orgs this is a harmless no-op when the badge
  // query isn't mounted.
  useEffect(() => {
    if (
      importStatus === ApiLearnerManualGoalSyncStatusChoices.Failure &&
      !awaitingFreshSync.current &&
      syncInfo &&
      !failureHandledRef.current
    ) {
      failureHandledRef.current = true;
      queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      setSyncInfo(null);
    }
  }, [importStatus, syncInfo, queryClient]);

  useEffect(() => {
    return () => {
      if (awaitingFreshSyncTimeout.current) clearTimeout(awaitingFreshSyncTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (isCompleted && syncInfo && !isOnRefreshingDataPage && !handledRef.current) {
      handledRef.current = true;
      const { clientFirstName, reportId } = syncInfo;
      setSyncInfo(null);

      toast(
        <ImportSuccessToast
          clientFirstName={clientFirstName}
          onGoToReassessment={() => {
            navigate(`/clinical-reports/reassessment/${reportId}/select-programming`);
            toast.dismiss({ containerId: BACKGROUND_SYNC_CONTAINER_ID });
          }}
        />,
        {
          containerId: BACKGROUND_SYNC_CONTAINER_ID,
          theme: "dark",
          autoClose: false,
          closeButton: true,
          closeOnClick: false,
        },
      );
    }
  }, [isCompleted, syncInfo, isOnRefreshingDataPage, navigate]);

  const startBackgroundSync = useCallback((info: SyncInfo) => {
    handledRef.current = false;
    failureHandledRef.current = false;
    awaitingFreshSync.current = true;
    if (awaitingFreshSyncTimeout.current) clearTimeout(awaitingFreshSyncTimeout.current);
    awaitingFreshSyncTimeout.current = setTimeout(() => {
      awaitingFreshSync.current = false;
    }, 30_000);
    setSyncInfo(info);
  }, []);

  const clearBackgroundSync = useCallback(() => {
    handledRef.current = true;
    failureHandledRef.current = true;
    if (awaitingFreshSyncTimeout.current) {
      clearTimeout(awaitingFreshSyncTimeout.current);
      awaitingFreshSyncTimeout.current = null;
    }
    setSyncInfo(null);
  }, []);

  return (
    <BackgroundSyncContext.Provider value={{ startBackgroundSync, clearBackgroundSync }}>
      {children}
      <ToastContainer
        containerId={BACKGROUND_SYNC_CONTAINER_ID}
        position="bottom-right"
        hideProgressBar
        style={{ width: "auto" }}
      />
    </BackgroundSyncContext.Provider>
  );
};
